import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSport, SPORT_META } from "@/context/SportContext";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TIER_COLOR = { group: "#007AFF", performance: "#FF4500", one_on_one: "#CCFF00" };

export default function PricingSettings() {
  const { enabledSports } = useSport();
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSport, setActiveSport] = useState("pickleball");

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data));
  }, []);

  useEffect(() => {
    if (!enabledSports.includes(activeSport) && enabledSports.length) setActiveSport(enabledSports[0]);
  }, [enabledSports, activeSport]);

  if (!pricing) return <div className="label-mono text-[#A3A3A3]">Loading…</div>;

  const sportPricing = pricing[activeSport] || {};

  const update = (tier, field, value) =>
    setPricing((p) => ({
      ...p,
      [activeSport]: { ...p[activeSport], [tier]: { ...p[activeSport][tier], [field]: value } },
    }));

  const updateBullet = (tier, idx, value) =>
    setPricing((p) => ({
      ...p,
      [activeSport]: {
        ...p[activeSport],
        [tier]: { ...p[activeSport][tier], bullets: p[activeSport][tier].bullets.map((b, i) => (i === idx ? value : b)) },
      },
    }));

  const addBullet = (tier) =>
    setPricing((p) => ({
      ...p,
      [activeSport]: { ...p[activeSport], [tier]: { ...p[activeSport][tier], bullets: [...(p[activeSport][tier].bullets || []), "New feature"] } },
    }));

  const removeBullet = (tier, idx) =>
    setPricing((p) => ({
      ...p,
      [activeSport]: { ...p[activeSport], [tier]: { ...p[activeSport][tier], bullets: p[activeSport][tier].bullets.filter((_, i) => i !== idx) } },
    }));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(pricing).map(([sport, tiers]) => [
          sport,
          Object.fromEntries(Object.entries(tiers).map(([k, v]) => [k, { ...v, from_price: Number(v.from_price) || 0 }])),
        ])
      );
      await api.put("/pricing", { pricing: cleaned });
      toast.success("Pricing saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="pricing-settings">
      <div className="flex items-end justify-between fade-up flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">SETTINGS · PRICING</div>
          <h1 className="h-display text-5xl tracking-tighter">Edit your tiers.</h1>
          <div className="text-sm text-[#A3A3A3] mt-2">Each sport has its own three tiers. Changes go live instantly.</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-pricing" className="btn-volt px-6 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save & publish"}
        </button>
      </div>

      {enabledSports.length > 1 && (
        <div className="inline-flex border border-[#262626]" data-testid="pricing-sport-tabs">
          {enabledSports.map((s) => {
            const m = SPORT_META[s];
            const active = s === activeSport;
            return (
              <button key={s} onClick={() => setActiveSport(s)} data-testid={`pricing-sport-${s}`}
                className="px-5 py-2.5 text-xs uppercase tracking-wider"
                style={active ? { background: m.color, color: "#0A0A0A", fontWeight: 700 } : { color: "#A3A3A3" }}>
                {m.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {["group", "performance", "one_on_one"].map((t) => {
          const x = sportPricing[t];
          if (!x) return null;
          return (
            <div key={t} className="card-surface p-6 space-y-4" data-testid={`tier-edit-${t}`}>
              <div className="flex items-center justify-between">
                <div className="label-mono" style={{ color: TIER_COLOR[t] }}>{t.replace("_", " ")}</div>
                <span className="w-3 h-3" style={{ background: TIER_COLOR[t] }} />
              </div>
              <Field label="Label" value={x.label} onChange={(v) => update(t, "label", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="From price (£)" type="number" value={x.from_price} onChange={(v) => update(t, "from_price", v)} />
                <Field label="Unit" value={x.unit} onChange={(v) => update(t, "unit", v)} placeholder="per hour" />
              </div>
              <Field label="Tagline" textarea value={x.tagline} onChange={(v) => update(t, "tagline", v)} />
              <div>
                <div className="label-mono mb-2">Bullets</div>
                <div className="space-y-2">
                  {(x.bullets || []).map((b, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={b} onChange={(e) => updateBullet(t, i, e.target.value)}
                        className="flex-1 bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
                      <button onClick={() => removeBullet(t, i)} className="px-2 text-[#FF4500] hover:opacity-70"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => addBullet(t)} className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add bullet
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, placeholder }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      {textarea ? (
        <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
      )}
    </div>
  );
}
